/**
 * rimuoviSfondo — toglie lo sfondo a tinta unita da un logo (Andrea 2026-09-17).
 *
 * I loghi caricati con lo sfondo (bianco, nero, colorato) in app restano un
 * quadrato pieno accanto a quelli ritagliati. Qui lo sfondo si toglie da
 * soli, nel browser, senza servizi esterni:
 *
 *   1. il colore di sfondo e' quello della cornice dell'immagine (mediana
 *      dei pixel di bordo). Se la cornice non e' di un colore solo (foto,
 *      sfumature) o e' gia' trasparente, non si tocca niente;
 *   2. "secchiello" dai bordi verso l'interno: diventa trasparente solo lo
 *      sfondo COLLEGATO al bordo. Il bianco dentro lo stemma resta;
 *   2b. anti-infiltrazione: le zone raggiunte solo da una fessura e di colore
 *      non proprio uguale al fondo sono pezzi di logo, e si rimettono;
 *   3. il contorno viene sfumato e ripulito dall'alone del vecchio sfondo;
 *   4. si ritaglia attorno a quel che resta, cosi' il logo riempie il badge.
 *
 * Non e' un'IA: con sfondi a foto, stoffa o molto sfumati non fa niente
 * (ritorna null) e il logo si carica com'e'. Per questo in Gestione Rosa,
 * al caricamento, c'e' l'anteprima prima/dopo con la scelta.
 */

/** Distanza RGB dal colore di sfondo (0-441) sotto cui un pixel e' sfondo
 * senza altre domande: assorbe il rumore JPEG di un fondo piatto. */
const TOLLERANZA_STRETTA = 18;
/** Oltre la stretta si accetta fino a qui (vignettature, fondi non proprio
 * piatti), ma solo avanzando "in piano": vedi SALTO_MASSIMO. */
const TOLLERANZA = 48;
/** Salto di colore massimo tra un pixel di sfondo e il vicino perche' anche
 * il vicino sia sfondo. Un fondo sfuma piano; il bordo di uno stemma, anche
 * scuro su fondo nero, e' uno scalino: li' il secchiello si ferma e non
 * entra nel logo. */
const SALTO_MASSIMO = 9;
/** Anti-infiltrazione: il secchiello puo' entrare nel logo da una fessura
 * (uno stemma scuro su fondo nero col bordo che in un punto sparisce). Le
 * zone raggiunte solo passando da strozzature piu' strette di 2*RAGGIO px
 * sono "sacche": se sono grandi e di colore non proprio uguale al fondo sono
 * pezzi di logo e si rimettono a posto. */
const RAGGIO_STROZZATURA = 3;
/** Sotto questa quota d'immagine una sacca e' solo un interstizio: resta sfondo. */
const QUOTA_SACCA = 0.004;
/** Distanza media dal colore di fondo oltre cui una sacca e' logo, non fondo. */
const DISTANZA_SACCA_LOGO = 10;
/** Ruvidita' massima del fondo = salto medio di colore tra pixel di sfondo
 * vicini. Un fondo piatto o sfumato sta sotto 2 anche con rumore JPEG; una
 * stoffa o una foto scura sta sopra 3, e li' il ritaglio verrebbe a brandelli:
 * meglio non toccare. (Tarato il 2026-09-17 sui loghi veri della lega.) */
const RUVIDITA_MASSIMA = 2.3;
/** Fino a qui il pixel di contorno e' un misto sfondo/logo: alfa parziale. */
const TOLLERANZA_SFUMATURA = 110;
/** Quota di cornice che deve essere del colore di sfondo. */
const QUOTA_CORNICE_UNIFORME = 0.8;
/** Se gia' cosi' tanta cornice e' trasparente, il logo e' gia' ritagliato. */
const QUOTA_CORNICE_TRASPARENTE = 0.5;
/** Lo sfondo tolto deve pesare almeno cosi' sull'immagine, o non vale la pena. */
const QUOTA_MINIMA_TOLTA = 0.03;
/** ...e non puo' essere quasi tutto: vorrebbe dire che si e' mangiato il logo. */
const QUOTA_MASSIMA_TOLTA = 0.97;

function mediana(valori: number[]): number {
  const v = [...valori].sort((a, b) => a - b);
  return v[Math.floor(v.length / 2)] ?? 0;
}

/** Indici (in pixel) della cornice esterna dell'immagine. */
function indiciCornice(w: number, h: number): number[] {
  const out: number[] = [];
  for (let x = 0; x < w; x++) {
    out.push(x, (h - 1) * w + x);
  }
  for (let y = 1; y < h - 1; y++) {
    out.push(y * w, y * w + w - 1);
  }
  return out;
}

/** Componenti connesse (4 direzioni) dei pixel per cui `dentro(i)` e' vero. */
function componenti(w: number, h: number, dentro: (i: number) => boolean): number[][] {
  const visto = new Uint8Array(w * h);
  const out: number[][] = [];
  for (let s = 0; s < w * h; s++) {
    if (visto[s] || !dentro(s)) continue;
    const comp = [s];
    visto[s] = 1;
    for (let k = 0; k < comp.length; k++) {
      const i = comp[k];
      const x = i % w;
      for (const j of [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, i - w, i + w]) {
        if (j < 0 || j >= w * h || visto[j] || !dentro(j)) continue;
        visto[j] = 1;
        comp.push(j);
      }
    }
    out.push(comp);
  }
  return out;
}

/** Minimo (erosione) o massimo (dilatazione) su un quadrato di lato 2r+1.
 * Fuori dall'immagine conta come sfondo (1). */
function filtroQuadrato(src: Uint8Array, w: number, h: number, r: number, erodi: boolean): Uint8Array {
  const passa = (inp: Uint8Array, orizzontale: boolean): Uint8Array => {
    const out = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let v = erodi ? 1 : 0;
        for (let d = -r; d <= r; d++) {
          const nx = orizzontale ? x + d : x;
          const ny = orizzontale ? y : y + d;
          const p = nx < 0 || ny < 0 || nx >= w || ny >= h ? 1 : inp[ny * w + nx];
          if (erodi ? p === 0 : p === 1) {
            v = p;
            break;
          }
        }
        out[y * w + x] = v;
      }
    }
    return out;
  };
  return passa(passa(src, true), false);
}

/** Anti-infiltrazione (vedi RAGGIO_STROZZATURA): toglie da `sfondo` le sacche
 * grandi raggiunte solo da una fessura, quando non hanno il colore del fondo. */
function rimettiSacche(
  sfondo: Uint8Array,
  w: number,
  h: number,
  distanza: (i: number) => number,
): void {
  const eroso = filtroQuadrato(sfondo, w, h, RAGGIO_STROZZATURA, true);
  // Sfondo "largo": quel che dell'eroso si raggiunge dalla cornice.
  const largo = new Uint8Array(w * h);
  const coda: number[] = [];
  const parti = (i: number) => {
    if (eroso[i] && !largo[i]) {
      largo[i] = 1;
      coda.push(i);
    }
  };
  for (const i of indiciCornice(w, h)) parti(i);
  for (let k = 0; k < coda.length; k++) {
    const i = coda[k];
    const x = i % w;
    if (x > 0) parti(i - 1);
    if (x < w - 1) parti(i + 1);
    if (i >= w) parti(i - w);
    if (i < w * (h - 1)) parti(i + w);
  }
  const riaperto = filtroQuadrato(largo, w, h, RAGGIO_STROZZATURA, false);
  const sacche = componenti(w, h, (i) => sfondo[i] === 1 && riaperto[i] === 0);
  for (const sacca of sacche) {
    if (sacca.length < w * h * QUOTA_SACCA) continue;
    let somma = 0;
    for (const i of sacca) somma += distanza(i);
    if (somma / sacca.length <= DISTANZA_SACCA_LOGO) continue;
    for (const i of sacca) sfondo[i] = 0;
  }
}

/**
 * Toglie lo sfondo dai pixel (modifica `data` sul posto).
 * Ritorna il riquadro del contenuto rimasto, o null se non c'era uno sfondo
 * a tinta unita da togliere (in quel caso `data` resta com'era).
 */
export function togliSfondoDaiPixel(
  data: Uint8ClampedArray,
  w: number,
  h: number,
): { x: number; y: number; w: number; h: number } | null {
  if (w < 8 || h < 8) return null;
  const cornice = indiciCornice(w, h);

  // Gia' ritagliato?
  const trasparenti = cornice.filter((i) => data[i * 4 + 3] < 128).length;
  if (trasparenti / cornice.length >= QUOTA_CORNICE_TRASPARENTE) return null;

  // Colore di sfondo = mediana della cornice opaca.
  const opachi = cornice.filter((i) => data[i * 4 + 3] >= 128);
  const br = mediana(opachi.map((i) => data[i * 4]));
  const bg = mediana(opachi.map((i) => data[i * 4 + 1]));
  const bb = mediana(opachi.map((i) => data[i * 4 + 2]));
  const distanza = (i: number): number => {
    const dr = data[i * 4] - br;
    const dg = data[i * 4 + 1] - bg;
    const db = data[i * 4 + 2] - bb;
    return Math.sqrt(dr * dr + dg * dg + db * db);
  };
  const uniformi = opachi.filter((i) => distanza(i) <= TOLLERANZA).length;
  // I pochi pixel di cornice gia' trasparenti contano come sfondo.
  if ((uniformi + trasparenti) / cornice.length < QUOTA_CORNICE_UNIFORME) return null;

  // Secchiello dai bordi (4 direzioni): sfondo = collegato alla cornice.
  const sfondo = new Uint8Array(w * h);
  const coda = new Int32Array(w * h);
  let testa = 0;
  let fine = 0;
  const salto = (i: number, j: number): number => {
    const dr = data[i * 4] - data[j * 4];
    const dg = data[i * 4 + 1] - data[j * 4 + 1];
    const db = data[i * 4 + 2] - data[j * 4 + 2];
    return Math.sqrt(dr * dr + dg * dg + db * db);
  };
  /** `da` = il pixel di sfondo da cui si arriva (-1 = si parte dalla cornice). */
  const prova = (i: number, da: number) => {
    if (sfondo[i]) return;
    if (data[i * 4 + 3] >= 128) {
      const d = distanza(i);
      if (d > TOLLERANZA) return;
      if (d > TOLLERANZA_STRETTA && da >= 0 && salto(i, da) > SALTO_MASSIMO) return;
    }
    sfondo[i] = 1;
    coda[fine++] = i;
  };
  for (const i of cornice) prova(i, -1);
  while (testa < fine) {
    const i = coda[testa++];
    const x = i % w;
    if (x > 0) prova(i - 1, i);
    if (x < w - 1) prova(i + 1, i);
    if (i >= w) prova(i - w, i);
    if (i < w * (h - 1)) prova(i + w, i);
  }
  let sommaSalti = 0;
  let coppie = 0;
  for (let i = 0; i < w * h; i++) {
    if (!sfondo[i]) continue;
    if (i % w < w - 1 && sfondo[i + 1]) {
      sommaSalti += salto(i, i + 1);
      coppie++;
    }
    if (i + w < w * h && sfondo[i + w]) {
      sommaSalti += salto(i, i + w);
      coppie++;
    }
  }
  if (coppie > 0 && sommaSalti / coppie > RUVIDITA_MASSIMA) return null;
  rimettiSacche(sfondo, w, h, distanza);
  let tolti = 0;
  for (let i = 0; i < w * h; i++) tolti += sfondo[i];
  const quotaTolta = tolti / (w * h);
  if (quotaTolta < QUOTA_MINIMA_TOLTA || quotaTolta > QUOTA_MASSIMA_TOLTA) return null;

  // Contorno: i pixel del logo che toccano lo sfondo sono un misto dei due
  // colori (antialias). Alfa in proporzione, e si toglie la quota di sfondo
  // dal colore, se no resta l'alone chiaro/scuro attorno allo stemma.
  const bgRgb = [br, bg, bb];
  const daSfumare: Array<[number, number]> = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (sfondo[i]) continue;
      let tocca = false;
      for (let dy = -1; dy <= 1 && !tocca; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          if (sfondo[ny * w + nx]) {
            tocca = true;
            break;
          }
        }
      }
      if (!tocca) continue;
      const d = distanza(i);
      if (d >= TOLLERANZA_SFUMATURA) continue;
      const a = Math.max(0.15, (d - TOLLERANZA) / (TOLLERANZA_SFUMATURA - TOLLERANZA));
      daSfumare.push([i, a]);
    }
  }
  for (const [i, a] of daSfumare) {
    for (let c = 0; c < 3; c++) {
      const v = (data[i * 4 + c] - (1 - a) * bgRgb[c]) / a;
      data[i * 4 + c] = Math.max(0, Math.min(255, Math.round(v)));
    }
    data[i * 4 + 3] = Math.round(data[i * 4 + 3] * a);
  }
  for (let i = 0; i < w * h; i++) {
    if (sfondo[i]) data[i * 4 + 3] = 0;
  }

  // Riquadro del contenuto rimasto.
  let x0 = w;
  let y0 = h;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > 8) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x1 < x0 || y1 < y0) return null;
  return { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/**
 * Da un'immagine gia' caricata a un canvas senza sfondo, ritagliato sul
 * contenuto. null = niente da togliere (foto, sfumature, gia' trasparente).
 */
export function canvasSenzaSfondo(img: HTMLImageElement, maxDim = 512): HTMLCanvasElement | null {
  let w = img.naturalWidth || img.width;
  let h = img.naturalHeight || img.height;
  if (!w || !h) return null;
  if (w > maxDim || h > maxDim) {
    const scala = maxDim / Math.max(w, h);
    w = Math.max(1, Math.round(w * scala));
    h = Math.max(1, Math.round(h * scala));
  }
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, w, h);
  let pixel: ImageData;
  try {
    pixel = ctx.getImageData(0, 0, w, h);
  } catch {
    return null; // canvas "sporco" (immagine da altro dominio): si lascia stare
  }
  const riquadro = togliSfondoDaiPixel(pixel.data, w, h);
  if (!riquadro) return null;
  ctx.putImageData(pixel, 0, 0);

  // Ritaglio sul contenuto, con un filo di margine.
  const margine = Math.round(Math.max(riquadro.w, riquadro.h) * 0.02);
  const out = document.createElement("canvas");
  out.width = riquadro.w + margine * 2;
  out.height = riquadro.h + margine * 2;
  const octx = out.getContext("2d");
  if (!octx) return null;
  octx.drawImage(
    canvas,
    riquadro.x,
    riquadro.y,
    riquadro.w,
    riquadro.h,
    margine,
    margine,
    riquadro.w,
    riquadro.h,
  );
  return out;
}
